/**
 * MilestoneTableView component - Table-based view for milestones list
 * 
 * Displays milestones in a sortable table format with columns for:
 * - Milestone name and description
 * - Project association
 * - Target date and status
 * - Completion percentage
 * - Action buttons (View, Edit, Delete)
 */

import React from 'react';
import { Eye, Edit, Trash2, AlertCircle } from 'lucide-react';
import DataTable, { TableColumn } from './DataTable';
import Button from './Button';
import Badge from './Badge';
import { Milestone } from '../types/milestone';
import { Project } from '../types/project';

interface MilestoneTableRow {
  id: string;
  name: string;
  description?: string;
  project: string;
  projectId: string;
  targetDate: string;
  status: 'upcoming' | 'in_progress' | 'completed' | 'overdue';
  completionPercentage: number;
  isOverdue: boolean;
  daysUntilDue?: number;
  createdAt: string;
}

interface MilestoneTableViewProps {
  milestones: Milestone[];
  projects: Project[];
  onView: (milestoneId: string) => void;
  onEdit: (milestone: Milestone) => void;
  onDelete: (milestoneId: string) => void;
}

const MilestoneTableView: React.FC<MilestoneTableViewProps> = ({
  milestones,
  projects,
  onView,
  onEdit,
  onDelete,
}) => {
  // Helper functions
  const getProjectName = (projectId: string): string => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown';
  };

  const calculateStatus = (targetDate: string, completionPercentage?: number | null): {
    status: 'upcoming' | 'in_progress' | 'completed' | 'overdue';
    isOverdue: boolean;
    daysUntilDue: number;
  } => {
    const now = new Date();
    const target = new Date(targetDate);
    const diffTime = target.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (completionPercentage === 100) {
      return { status: 'completed', isOverdue: false, daysUntilDue: diffDays };
    }
    
    if (diffDays < 0) {
      return { status: 'overdue', isOverdue: true, daysUntilDue: Math.abs(diffDays) };
    }
    
    if (completionPercentage && completionPercentage > 0) {
      return { status: 'in_progress', isOverdue: false, daysUntilDue: diffDays };
    }
    
    return { status: 'upcoming', isOverdue: false, daysUntilDue: diffDays };
  };

  const getStatusBadge = (status: 'upcoming' | 'in_progress' | 'completed' | 'overdue') => {
    const statusMap = {
      upcoming: 'Upcoming',
      in_progress: 'In Progress',
      completed: 'Completed',
      overdue: 'Overdue',
    };
    
    const label = statusMap[status];
    
    return (
      <Badge status={status as any} size="sm">
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Transform milestones into table rows
  const tableData: MilestoneTableRow[] = milestones.map(milestone => {
    const statusInfo = calculateStatus(milestone.target_date, milestone.completion_percentage);
    return {
      id: milestone.id,
      name: milestone.name,
      description: milestone.description ?? undefined,
      project: getProjectName(milestone.project_id),
      projectId: milestone.project_id,
      targetDate: milestone.target_date,
      status: statusInfo.status,
      completionPercentage: milestone.completion_percentage || 0,
      isOverdue: statusInfo.isOverdue,
      daysUntilDue: statusInfo.daysUntilDue,
      createdAt: milestone.created_at,
    };
  });

  // Define table columns
  const columns: TableColumn<MilestoneTableRow>[] = [
    {
      key: 'name',
      label: 'Milestone',
      sortable: true,
      className: 'font-medium text-gray-900 dark:text-white min-w-[200px]',
      render: (row) => (
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold">{row.name}</span>
            {row.isOverdue && (
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
            )}
          </div>
          {row.description && (
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      className: 'text-sm',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.project}</span>
      ),
    },
    {
      key: 'targetDate',
      label: 'Target Date',
      sortable: true,
      sortValue: (row) => row.targetDate,
      className: 'text-sm',
      render: (row) => (
        <div>
          <div className={`font-medium ${row.isOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
            {formatDate(row.targetDate)}
          </div>
          {row.isOverdue ? (
            <div className="text-xs text-red-600 dark:text-red-400 font-semibold">
              {row.daysUntilDue} {row.daysUntilDue === 1 ? 'day' : 'days'} overdue
            </div>
          ) : row.status !== 'completed' && (
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {row.daysUntilDue} {row.daysUntilDue === 1 ? 'day' : 'days'} remaining
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => getStatusBadge(row.status),
    },
    {
      key: 'completionPercentage',
      label: 'Progress',
      sortable: true,
      sortValue: (row) => row.completionPercentage,
      className: 'min-w-[150px]',
      render: (row) => (
        <div className="space-y-1">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            {row.completionPercentage}%
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                row.status === 'completed' 
                  ? 'bg-green-500' 
                  : row.isOverdue 
                  ? 'bg-red-500' 
                  : 'bg-gradient-to-r from-primary-500 to-secondary-500'
              }`}
              style={{ width: `${row.completionPercentage}%` }}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right min-w-[180px]',
      render: (row) => {
        const milestone = milestones.find(m => m.id === row.id)!;
        return (
          <div className="flex gap-1 justify-end">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={Eye}
              onClick={() => onView(row.id)}
              title="View Details"
            >
              View
            </Button>
            <Button
              variant="primary"
              size="sm"
              leftIcon={Edit}
              onClick={() => onEdit(milestone)}
              title="Edit Milestone"
            >
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              leftIcon={Trash2}
              onClick={() => onDelete(row.id)}
              title="Delete Milestone"
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-xl shadow-lg p-6">
      <DataTable
        columns={columns}
        data={tableData}
        emptyMessage="No milestones found"
        striped={true}
        getRowKey={(row) => row.id}
        defaultSortKey="targetDate"
        defaultSortDirection="asc"
        onRowClick={(row) => onView(row.id)}
      />
    </div>
  );
};

export default MilestoneTableView;
